/**
 * Merge UI metadata rows onto a hand-built AntD column array.
 *
 * WHY NOT BUILD THE COLUMNS FROM METADATA OUTRIGHT
 * The library's `metadataToAntdColumns` does that, and it is right for a grid
 * whose cells are values. This grid's cells are not: Type and Status are
 * coloured tags, Reported is a relative time with a clock-skew warning, Replay
 * is a link to a session recording, Details is a clipped line with the full
 * text on hover. Those renders are behaviour and belong in code.
 *
 * What does NOT belong in code is what a column is CALLED, how wide it is,
 * what order it sits in, whether it is shown, and whether it offers a filter.
 * Those are presentation decisions that cost a frontend release in every
 * consuming app (wig-broker-deals#189). So the columns keep their renders and
 * take the rest from data.
 *
 * ABSENT METADATA IS NOT AN ERROR. A column with no row keeps exactly what it
 * was given, and a page whose metadata call fails renders precisely as it did
 * before this existed. That is deliberate: the metadata lives in a different
 * de-hub module and a different database, and a shared admin table being
 * unreachable must not take a user's feedback queue down with it.
 */

/**
 * @param {Array<object>} columns   AntD columns, already built with renders
 * @param {Array<object>} metadata  rows from `{base}/metadata?table_name=`
 * @param {object} [options]
 * @param {Record<string,string>} [options.alias]
 *   column key -> metadata `field`, for the columns whose display name differs
 *   from the database column behind it. Stated at the call site so the rename
 *   is visible where it happens rather than implied by a lookup that silently
 *   misses.
 * @returns {Array<object>} new column objects; the input is not mutated
 */
export const applyColumnMetadata = (columns, metadata, options = {}) => {
  const cols = Array.isArray(columns) ? columns : [];
  const rows = Array.isArray(metadata) ? metadata : [];
  if (rows.length === 0) return cols;

  const alias = options.alias || {};
  const byField = new Map();
  for (const row of rows) {
    if (row && row.field) byField.set(String(row.field), row);
  }

  /** The metadata row describing this column, if any. */
  const rowFor = (col) => {
    const key = col?.key ?? col?.dataIndex;
    if (key == null) return undefined;
    const aliased = alias[String(key)];
    if (aliased) return byField.get(String(aliased));
    return byField.get(String(key));
  };

  const out = [];
  cols.forEach((col, index) => {
    const row = rowFor(col);
    if (!row) {
      // No opinion recorded. Keep the column exactly as handed over, and keep
      // its position — `ord` only reorders the columns metadata knows about.
      out.push({ col, ord: null, index });
      return;
    }
    if (row.hide === true) return;

    const next = { ...col };
    if (row.label) next.title = row.label;
    if (row.width != null) next.width = row.width;
    if (row.filterable !== null && row.filterable !== undefined) {
      next.filterable = row.filterable;
    }
    out.push({ col: next, ord: row.ord ?? null, index });
  });

  // Sort only where `ord` is set, and keep everything else where it was: a
  // partially-curated table must not shuffle the columns nobody has ordered.
  const anyOrd = out.some((e) => e.ord != null);
  if (!anyOrd) return out.map((e) => e.col);

  return [...out]
    .sort((a, b) => {
      if (a.ord != null && b.ord != null) return a.ord - b.ord;
      // An ordered column and an unordered one: leave their relative position
      // alone rather than inventing one.
      return a.index - b.index;
    })
    .map((e) => e.col);
};

export default applyColumnMetadata;
